import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Check, Clock, UserCog, FileText, XCircle, Hammer, Download,
    AlertTriangle, ChevronRight, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
    PRODUCTO_LABEL, PASOS, ESTADO_LABEL, ESTADO_BADGE, fmtMXN, fmtDate,
    slaColor,
} from "./_common";

export default function SolicitudDetalle() {
    const { folio } = useParams();
    const navigate = useNavigate();
    const [sol, setSol] = useState(null);
    const [timeline, setTimeline] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("solicitante");

    const [notasPaso, setNotasPaso] = useState("");
    const [actPaso, setActPaso] = useState(null);  // {paso}
    const [actResp, setActResp] = useState(false);
    const [respEmail, setRespEmail] = useState("");
    const [actDeclinar, setActDeclinar] = useState(false);
    const [motivoDecl, setMotivoDecl] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/aceptacion/pipeline/solicitudes/${folio}`);
            setSol(data);
            try {
                const { data: tl } = await api.get(
                    `/aceptacion/pipeline/solicitudes/${data.id}/timeline`
                );
                setTimeline(tl);
            } catch { /* timeline opcional */ }
        } catch (err) {
            toast.error("No se pudo cargar la solicitud");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folio]);

    if (!sol) {
        return (
            <div className="p-8 lg:p-10">
                {loading ? "Cargando..." : "Solicitud no encontrada"}
            </div>
        );
    }

    const pasosCompletados = (sol.pasos || []).filter((p) => p.estado === "completado").length;
    const slaDias = sol.sla_dias_habiles || (sol.complejidad === "compleja" ? 20 : 10);
    const siguientePaso = pasosCompletados + 1;
    const puedeEditar = sol.estado_general === "en_proceso";
    const folioSol = sol.folio_f01 || sol.id;

    const completarPaso = async () => {
        if (notasPaso.length > 1500) {
            toast.error("Notas: máximo 1500 caracteres"); return;
        }
        try {
            await api.post(
                `/aceptacion/pipeline/solicitudes/${sol.id}/completar-paso`,
                { paso: actPaso.paso, notas: notasPaso },
            );
            toast.success(`Paso ${actPaso.paso} completado`);
            setActPaso(null);
            setNotasPaso("");
            load();
        } catch (err) {
            toast.error("No se pudo completar paso", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const asignarResp = async () => {
        if (!respEmail.includes("@")) {
            toast.error("Email inválido"); return;
        }
        try {
            await api.post(
                `/aceptacion/pipeline/solicitudes/${sol.id}/asignar-responsable`,
                { responsable_email: respEmail },
            );
            toast.success("Responsable asignado");
            setActResp(false);
            setRespEmail("");
            load();
        } catch (err) {
            toast.error("No se pudo asignar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const declinar = async () => {
        if (motivoDecl.length < 5) {
            toast.error("Motivo: mínimo 5 caracteres"); return;
        }
        try {
            await api.post(
                `/aceptacion/pipeline/solicitudes/${sol.id}/declinar-solicitante`,
                { motivo: motivoDecl },
            );
            toast.success("Solicitud declinada por el solicitante");
            setActDeclinar(false);
            setMotivoDecl("");
            load();
        } catch (err) {
            toast.error("No se pudo declinar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const emitirCartaDeclinacion = async () => {
        try {
            const { data } = await api.post(
                `/aceptacion/pipeline/solicitudes/${sol.id}/emitir-carta-declinacion`
            );
            toast.success(`Carta ${data.folio} emitida`);
            window.open(
                `${api.defaults.baseURL}/aceptacion/pipeline/solicitudes/${sol.id}/carta-declinacion.pdf`,
                "_blank",
            );
            load();
        } catch (err) {
            toast.error("No se pudo emitir carta", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1400px] space-y-6" data-testid="solicitud-detalle-page">
            {/* Header */}
            <div className="flex items-start gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate("/aceptacion/pipeline")}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Pipeline
                </Button>
                <div className="flex-1">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-xs text-gray-500 mb-1">{folioSol}</div>
                            <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                                {sol.solicitante?.razon_social}
                            </h1>
                            <div className="text-sm text-gray-600 mt-1">
                                {sol.producto_solicitado} · {PRODUCTO_LABEL[sol.producto_solicitado]}
                                 · <strong>{fmtMXN(sol.patrimonio_estimado_mxn)}</strong>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs px-3 py-1 rounded border ${ESTADO_BADGE[sol.estado_general] || "bg-gray-100"}`}>
                                {ESTADO_LABEL[sol.estado_general] || sol.estado_general}
                            </span>
                            <span className={`text-xs px-3 py-1 rounded border inline-flex items-center gap-1 ${slaColor(sol.dias_en_proceso, sol.dentro_de_sla, slaDias)}`}>
                                <Clock className="h-3 w-3" />
                                {sol.dentro_de_sla === false ? "SLA VENCIDO" : `${sol.dias_en_proceso || 0}/${slaDias} días`}
                            </span>
                            <Button variant="outline" size="sm" onClick={load}>
                                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stepper horizontal */}
            <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="stepper">
                <div className="flex items-center">
                    {PASOS.map((nombre, i) => {
                        const n = i + 1;
                        const paso = (sol.pasos || [])[i];
                        const estado = paso?.estado || "pendiente";
                        const isCompleto = estado === "completado";
                        const isActivo = n === siguientePaso && puedeEditar;
                        return (
                            <div key={n} className="flex items-center flex-1 last:flex-none">
                                <div className="flex flex-col items-center text-center flex-1">
                                    <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 ${
                                        isCompleto ? "bg-emerald-500 border-emerald-500 text-white" :
                                        isActivo ? "bg-blue-500 border-blue-500 text-white" :
                                        "bg-gray-50 border-gray-300 text-gray-400"
                                    }`}>
                                        {isCompleto ? <Check className="h-5 w-5" /> : <span className="text-sm font-bold">{n}</span>}
                                    </div>
                                    <div className={`text-[11px] mt-2 leading-tight max-w-[90px] ${isActivo ? "font-semibold text-blue-700" : "text-gray-600"}`}>
                                        {nombre}
                                    </div>
                                    {paso?.fecha_completado && (
                                        <div className="text-[10px] text-gray-400 mt-1">
                                            {fmtDate(paso.fecha_completado)}
                                        </div>
                                    )}
                                </div>
                                {n < 7 && (
                                    <div className={`h-0.5 flex-1 mx-1 ${isCompleto ? "bg-emerald-400" : "bg-gray-200"}`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="solicitante" data-testid="tab-solicitante">Solicitante</TabsTrigger>
                    <TabsTrigger value="producto">Producto & negocio</TabsTrigger>
                    <TabsTrigger value="analisis" data-testid="tab-analisis">Análisis</TabsTrigger>
                    <TabsTrigger value="dictamen">Dictamen</TabsTrigger>
                    <TabsTrigger value="resolucion">Resolución</TabsTrigger>
                </TabsList>

                <TabsContent value="solicitante" className="mt-4 space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-6 grid md:grid-cols-2 gap-5 text-sm">
                        <KV k="Razón social" v={sol.solicitante?.razon_social} />
                        <KV k="RFC" v={sol.solicitante?.rfc} />
                        <KV k="Contacto" v={sol.solicitante?.contacto_nombre} />
                        <KV k="Email" v={sol.solicitante?.contacto_email} />
                        <KV k="Teléfono" v={sol.solicitante?.contacto_telefono} />
                        <KV k="Origen" v={sol.origen} />
                        {sol.lead_id && (
                            <KV k="Lead origen" v={<Link to="/leads" className="text-blue-600 hover:underline font-mono text-xs">{sol.lead_id}</Link>} />
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="producto" className="mt-4 space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-6 grid md:grid-cols-2 gap-5 text-sm">
                        <KV k="Producto" v={`${sol.producto_solicitado} · ${PRODUCTO_LABEL[sol.producto_solicitado]}`} />
                        <KV k="Complejidad" v={`${sol.complejidad} · SLA ${slaDias}d`} />
                        <KV k="Patrimonio estimado" v={fmtMXN(sol.patrimonio_estimado_mxn)} />
                        <KV k="Moneda" v={sol.moneda} />
                        <KV k="Fecha solicitud" v={fmtDate(sol.fecha_solicitud || sol.created_at)} />
                        <KV k="Límite dictamen" v={fmtDate(sol.fecha_limite_dictamen)} />
                        <KV k="Resolución final" v={sol.resolucion_final || "—"} />
                        <div className="md:col-span-2">
                            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                                Descripción del negocio
                            </div>
                            <div className="whitespace-pre-wrap text-sm text-gray-800">
                                {sol.descripcion_negocio}
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="analisis" className="mt-4 space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-6">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <h3 className="font-display text-lg">Avance de pasos</h3>
                                <p className="text-xs text-gray-500">Secuencial · un paso a la vez.</p>
                            </div>
                            {!sol.responsable_email && puedeEditar && (
                                <Button size="sm" variant="outline" onClick={() => setActResp(true)}>
                                    <UserCog className="h-4 w-4 mr-2" /> Asignar responsable
                                </Button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {PASOS.map((nombre, i) => {
                                const n = i + 1;
                                const paso = (sol.pasos || [])[i];
                                const estado = paso?.estado || "pendiente";
                                const esSiguiente = n === siguientePaso;
                                return (
                                    <div key={n} className={`flex items-center gap-3 p-3 rounded border ${estado === "completado" ? "bg-emerald-50/50 border-emerald-200" : esSiguiente ? "bg-blue-50/60 border-blue-200" : "bg-gray-50 border-gray-200"}`}>
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${estado === "completado" ? "bg-emerald-500 text-white" : esSiguiente ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-600"}`}>
                                            {estado === "completado" ? <Check className="h-4 w-4" /> : n}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{nombre}</div>
                                            {paso?.fecha_completado && (
                                                <div className="text-xs text-gray-500">
                                                    Completado {fmtDate(paso.fecha_completado)}
                                                    {paso.responsable && ` · ${paso.responsable}`}
                                                </div>
                                            )}
                                            {paso?.notas && (
                                                <div className="text-xs text-gray-600 italic mt-1">
                                                    {paso.notas}
                                                </div>
                                            )}
                                        </div>
                                        {esSiguiente && puedeEditar && n < 6 && (
                                            <Button size="sm"
                                                onClick={() => setActPaso({ paso: n })}
                                                data-testid={`completar-paso-${n}`}
                                            >
                                                Completar paso {n}
                                            </Button>
                                        )}
                                        {esSiguiente && n === 6 && puedeEditar && (
                                            <Button size="sm" variant="outline"
                                                onClick={() => navigate("/gobierno/comite/sesiones")}>
                                                Agendar en Comité <ChevronRight className="h-3 w-3 ml-1" />
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="dictamen" className="mt-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-6">
                        {sol.dictamen_f09_id ? (
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Dictamen F-09 asociado</div>
                                    <div className="font-mono text-sm">{sol.dictamen_f09_id}</div>
                                </div>
                                <Link to={`/aceptacion/dictamenes/${sol.dictamen_f09_id}`}>
                                    <Button variant="outline" size="sm">
                                        <FileText className="h-4 w-4 mr-2" /> Ver dictamen
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-500">
                                <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
                                <p className="text-sm">Aún no hay dictamen F-09 emitido.</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    El dictamen se genera al cerrar la sesión del Comité Fiduciario (Paso 6).
                                </p>
                                {puedeEditar && pasosCompletados >= 5 && (
                                    <Button size="sm" variant="outline" className="mt-4"
                                        onClick={() => navigate("/gobierno/comite/sesiones")}>
                                        Agendar en Comité
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="resolucion" className="mt-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-5">
                            <KV k="Estado general" v={ESTADO_LABEL[sol.estado_general]} />
                            <KV k="Resolución final" v={sol.resolucion_final || "—"} />
                            <KV k="Fecha resolución" v={fmtDate(sol.fecha_resolucion)} />
                        </div>
                        <div className="pt-4 border-t flex gap-3 flex-wrap">
                            {(sol.estado_general === "rechazado" || sol.resolucion_final === "rechazado") && (
                                <Button variant="outline" onClick={emitirCartaDeclinacion}
                                    data-testid="emitir-carta-declinacion">
                                    <Download className="h-4 w-4 mr-2" /> Emitir carta de declinación
                                </Button>
                            )}
                            {(sol.estado_general === "aprobado" || sol.estado_general === "aprobado_con_condiciones")
                                && !sol.fideicomiso_id && (
                                <Button onClick={() => navigate("/fideicomisos/nuevo?dictamen=" + sol.dictamen_f09_id)}>
                                    <Hammer className="h-4 w-4 mr-2" /> Promover a fideicomiso
                                </Button>
                            )}
                            {sol.fideicomiso_id && (
                                <Link to={`/fideicomisos/${sol.fideicomiso_id}`}>
                                    <Button variant="outline">
                                        <FileText className="h-4 w-4 mr-2" /> Ver fideicomiso creado
                                    </Button>
                                </Link>
                            )}
                            {sol.carta_declinacion_id && (
                                <Button variant="outline"
                                    onClick={() => window.open(
                                        `${api.defaults.baseURL}/aceptacion/pipeline/solicitudes/${sol.id}/carta-declinacion.pdf`,
                                        "_blank",
                                    )}>
                                    <FileText className="h-4 w-4 mr-2" /> Ver carta de declinación
                                </Button>
                            )}
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Botón declinar flotante */}
            {puedeEditar && (
                <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setActDeclinar(true)}
                        className="text-red-600 hover:text-red-700" data-testid="btn-declinar">
                        <XCircle className="h-4 w-4 mr-2" /> Declinar por el solicitante
                    </Button>
                </div>
            )}

            {/* Dialog completar paso */}
            <Dialog open={!!actPaso} onOpenChange={() => setActPaso(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Completar paso {actPaso?.paso} · {PASOS[(actPaso?.paso || 1) - 1]}</DialogTitle>
                    </DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-600">Notas (obligatorio)</label>
                        <textarea
                            value={notasPaso} onChange={(e) => setNotasPaso(e.target.value)}
                            maxLength={1500}
                            className="w-full min-h-[120px] rounded-md border p-3 text-sm"
                            placeholder="Resumen de la validación hecha en este paso"
                            data-testid="paso-notas"
                        />
                        <div className="text-[10px] text-gray-400 text-right">{notasPaso.length}/1500</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActPaso(null)}>Cancelar</Button>
                        <Button onClick={completarPaso} data-testid="paso-submit">Completar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog asignar responsable */}
            <Dialog open={actResp} onOpenChange={setActResp}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar responsable</DialogTitle>
                    </DialogHeader>
                    <Input placeholder="email@trafalgar.mx" value={respEmail}
                        onChange={(e) => setRespEmail(e.target.value)} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActResp(false)}>Cancelar</Button>
                        <Button onClick={asignarResp}>Asignar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog declinar */}
            <Dialog open={actDeclinar} onOpenChange={setActDeclinar}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Declinación por el solicitante</DialogTitle>
                    </DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-600">Motivo</label>
                        <textarea value={motivoDecl} onChange={(e) => setMotivoDecl(e.target.value)}
                            className="w-full min-h-[100px] rounded-md border p-3 text-sm" minLength={5} maxLength={2000} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActDeclinar(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={declinar}>Declinar solicitud</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-0.5">{k}</div>
            <div className="text-sm text-gray-900">{v || "—"}</div>
        </div>
    );
}
